import * as React from "react";
import { Typography, Box, useMediaQuery, useTheme } from "@mui/material";
import { Image, Table, Tag } from "antd";
import { useTranslation } from "react-i18next";
import NewCard from "./NewCard";

const getInboundStatusKey = (status) => {
  if (status === "Pending") return "inbounds.status.pending";
  if (status === "Partially Received") return "inbounds.status.partiallyReceived";
  if (status === "Received") return "inbounds.status.received";
  return null;
};

export const ReturnInboundSection = ({ order }) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  if (!order?.returnInbound) {
    return null;
  }

  const { returnInbound, returnInboundItems = [] } = order;
  const statusKey = getInboundStatusKey(returnInbound.status);

  const tableData = returnInboundItems.map((row) => ({
    key: row._id,
    _id: row._id,
    sku: row.product?.sku || row.product?.externalId,
    image: row.product?.image,
    name: row.product?.name,
    quantity: row.quantity,
    quantityReceived: row.quantityReceived ?? 0,
  }));

  const columns = [
    {
      title: t("common.image"),
      dataIndex: "image",
      render: (text) => <Image width={50} height={50} src={text} preview={true} />,
    },
    {
      title: t("common.name"),
      dataIndex: "name",
    },
    {
      title: t("common.sku"),
      dataIndex: "sku",
    },
    {
      title: t("orderDetails.returnInbound.qtyRequested"),
      dataIndex: "quantity",
    },
    {
      title: t("orderDetails.returnInbound.qtyReceived"),
      dataIndex: "quantityReceived",
    },
    {
      title: t("orderDetails.returnInbound.qtyRemaining"),
      render: (_, { quantity, quantityReceived }) =>
        parseInt(quantity, 10) - parseInt(quantityReceived, 10),
    },
  ];

  return (
    <NewCard
      sx={{ mb: 3, mt: 2 }}
      content={
        <Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2, flexWrap: "wrap" }}>
            <Typography variant="h6" sx={{ color: "var(--secColor4)", fontWeight: "bold" }}>
              {t("orderDetails.returnInbound.title")}
            </Typography>
            {statusKey && <Tag color="blue">{t(statusKey)}</Tag>}
          </Box>

          {isMobile ? (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              {tableData.map((item) => (
                <Box
                  key={item._id}
                  sx={{
                    p: 2,
                    backgroundColor: "var(--secColor2)",
                    borderRadius: 2,
                    border: "1px solid var(--secColor2)",
                  }}
                >
                  <Typography variant="subtitle1" fontWeight="bold" sx={{ color: "var(--secColor4)", mb: 1 }}>
                    {item.name}
                  </Typography>
                  <Typography variant="body2" sx={{ color: "var(--textSecondary)", mb: 0.5 }}>
                    {t("common.sku")}: {item.sku}
                  </Typography>
                  <Typography variant="body2" sx={{ color: "var(--secColor4)" }}>
                    {t("orderDetails.returnInbound.qtyRequested")}: {item.quantity}
                  </Typography>
                  <Typography variant="body2" sx={{ color: "var(--secColor4)" }}>
                    {t("orderDetails.returnInbound.qtyReceived")}: {item.quantityReceived}
                  </Typography>
                  <Typography variant="body2" sx={{ color: "var(--secColor4)" }}>
                    {t("orderDetails.returnInbound.qtyRemaining")}:{" "}
                    {parseInt(item.quantity, 10) - parseInt(item.quantityReceived, 10)}
                  </Typography>
                </Box>
              ))}
            </Box>
          ) : (
            <Table columns={columns} dataSource={tableData} pagination={false} />
          )}
        </Box>
      }
    />
  );
};

export default ReturnInboundSection;
